/**
 * The fields a postal address carries, in the order somebody writes them.
 *
 * This is a copy of a fact the server's schemas own, and it is here because the
 * address editor cannot get it from the searchable surface: that surface
 * publishes what a query may name, which for a house is `location.city` and
 * `location.region` and nothing else. A form drawn from it would offer two
 * boxes out of six.
 *
 * The copy is held honest by a gate rather than a promise —
 * `test/unit/patchSurface.test.js` checks this list against the server's own
 * OpenAPI document, captured by `npm run capture-patch-surface`.
 */
export const ADDRESS_FIELDS = Object.freeze([
    'addressLine1',
    'addressLine2',
    'city',
    'region',
    'postalCode',
    'country'
])

/**
 * The five a stored address must carry.
 *
 * `addressLine2` is the odd one out on purpose: a unit designator is absent for
 * a single-unit property, and the authoritative data never answers on it.
 */
export const REQUIRED_ADDRESS_FIELDS = Object.freeze(
    ADDRESS_FIELDS.filter((field) => field !== 'addressLine2')
)

/**
 * What each field is called on screen.
 *
 * Named for the person filling the form rather than derived from the schema
 * path. `labelFor` would answer "Address line 1" and "Postal code" — correct,
 * and not what anybody calls them. An address is the one shape common enough to
 * have its own words.
 */
export const ADDRESS_LABELS = Object.freeze({
    addressLine1: 'Street address',
    addressLine2: 'Apt / Suite / Floor',
    city: 'City',
    region: 'State / Region',
    postalCode: 'ZIP / Postal code',
    country: 'Country'
})

/**
 * The countries the dropdown offers.
 *
 * **An assumption, not a ruling.** Nothing in this repository states which
 * countries the platform serves; the licence discussion in the administration
 * requirements is US-per-state with "other countries differ or have no
 * equivalent" beside it. So this is the shortest list that is not wrong, and
 * adding a country is one line here.
 *
 * The codes are ISO 3166 alpha-2, which is what `Country.Code2` answers and
 * what every stored address holds.
 */
export const COUNTRIES = Object.freeze([
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' }
])

/** The country a new address opens on. Same assumption as the list above. */
export const DEFAULT_COUNTRY = 'US'

function filled(value) {
    return typeof value === 'string' && value.trim() !== ''
}

/**
 * An address as the lines somebody would write on an envelope.
 *
 * Empty lines are dropped rather than drawn as a dash, and the locality line
 * joins three short fields that have no business each taking a row: "Newark,
 * NY 14513". A field the address does not carry simply is not there, so a
 * partial address still reads as far as it goes.
 *
 * The country is spelled out where the list knows it, because "US" on a line by
 * itself reads like a code rather than a place.
 */
export function addressLines(address) {
    if (address === null || address === undefined) {
        return []
    }
    const locality = [
        address.city,
        [address.region, address.postalCode].filter(filled).join(' ')
    ].filter(filled).join(', ')
    const country = COUNTRIES.find((each) => each.code === address.country)
    return [
        address.addressLine1,
        address.addressLine2,
        locality,
        country === undefined ? address.country : country.name
    ].filter(filled)
}
